import React from 'react';
import { parseVideoUrl, buildEmbedSrc } from '../../utils/videoEmbeds';
import { isTauri } from '../../utils/tauri';

// Recognised TLDs for bare "domain.tld" detection (no scheme, no www.) below —
// a curated set rather than the full IANA list, tuned to catch common gTLDs
// and ccTLDs while keeping false positives (e.g. "config.yaml", "Node.js",
// "e.g.", "v2.0.1") out. Extend as real cases come up.
export const KNOWN_TLDS =
  'com|net|org|io|dev|co|app|ai|edu|gov|info|biz|me|tv|xyz|online|site|tech|store|shop|blog|news|club|live|world|life|design|cloud|link|space|fun|top|pro|name|mobi|asia|ba|uk|us|ca|de|fr|es|it|nl|se|no|dk|fi|pl|cz|at|ch|be|pt|gr|ru|ua|by|hr|rs|si|sk|hu|ro|bg|tr|jp|cn|kr|in|au|nz|br|mx|ar|cl|za|il|sa|ae|eg|ke|id|my|sg|ph|th|vn|pk|bd|hk|tw';

// Matches, in order: http(s) URLs, bare www. domains, and bare "domain.tld"
// tokens whose TLD is in KNOWN_TLDS — so a plain-typed hostname like
// "example.com" or "klixa.ba" still auto-previews without a scheme. Each
// alternative is wrapped in its own capturing group so callers can recover
// the actual matched text via m[1]||m[2]||m[3] below.
//
// The third alternative avoids a negative lookbehind (unsupported in older
// WebKit/JavaScriptCore, which the Tauri webview can be pinned to) by
// consuming one boundary character via `(?:^|[^\w@./-])` instead of
// asserting on it zero-width — that's what keeps it from matching
// mid-path/mid-email fragments (a@b.com, path/to/file.com). Because that
// boundary character is consumed as part of m[0], `m.index` points one
// character *before* the actual domain text for that branch; callers must
// add back `m[0].length - raw.length` to get the true start offset.
// Everything stops before trailing sentence punctuation so a URL at the end
// of a sentence doesn't swallow the period.
export const URL_REGEX = new RegExp(
  '(https?:\\/\\/[^\\s<]+[^\\s<.,:;!?"\')\\]}])' +
    '|(www\\.[^\\s<]+[^\\s<.,:;!?"\')\\]}])' +
    `|(?:^|[^\\w@./-])((?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\\.)+(?:${KNOWN_TLDS})(?:\\/[^\\s<]*[^\\s<.,:;!?"')\\]}])?)(?![a-z0-9-])`,
  'gi'
);

// Shared by linkifyText/firstHref: pull the matched URL text out of whichever
// of URL_REGEX's three alternatives fired, and normalise it to an absolute
// http(s) href (www./bare-domain matches have no scheme).
export function resolveUrlMatch(m) {
  const raw = m[1] || m[2] || m[3];
  const href = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
  return { raw, href };
}

export function openExternalUrl(url) {
  if (!url) return;
  if (isTauri()) {
    import('@tauri-apps/plugin-shell')
      .then(({ open }) => open(url))
      .catch(() => window.open(url, '_blank', 'noopener,noreferrer'));
  } else {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

// Split plain message text into text + clickable-link segments. Only http/https
// (and www. normalised to https) are linked, so no javascript:/file: schemes.
export function linkifyText(text) {
  if (!text) return text;
  const out = [];
  let last = 0;
  let m;
  URL_REGEX.lastIndex = 0;
  while ((m = URL_REGEX.exec(text)) !== null) {
    const { raw, href } = resolveUrlMatch(m);
    const start = m.index + (m[0].length - raw.length);
    if (start > last) out.push(text.slice(last, start));
    out.push(
      <a
        key={`${start}-${raw}`}
        className="chat-msg-link"
        href={href}
        onClick={(e) => {
          e.preventDefault();
          openExternalUrl(href);
        }}
      >
        {raw}
      </a>
    );
    last = start + raw.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

// First linkable URL in the text, normalised to an absolute http(s) href, or
// null. Used to pick which link (if any) to unfurl into a preview card.
export function firstHref(text) {
  if (!text) return null;
  URL_REGEX.lastIndex = 0;
  const m = URL_REGEX.exec(text);
  if (!m) return null;
  return resolveUrlMatch(m).href;
}

// A URL that points straight at an image/gif (by extension, tolerating a query
// string or fragment) — embedded inline rather than unfurled into a card.
export function isImageUrl(url) {
  if (typeof url !== 'string') return false;
  const path = url.split(/[?#]/)[0];
  return /\.(gif|png|jpe?g|webp|avif|bmp|svg)$/i.test(path);
}

// A YouTube or Vimeo link — resolved to an embeddable player src, or null.
// Covers youtube.com/watch?v=, youtu.be/, youtube.com/shorts/, and vimeo.com/.
// The parsing itself lives in utils/videoEmbeds.js (shared with the documents
// video node and the sanitizer); output here is pinned byte-identical to the
// historical inline parser by videoEmbeds.test.js.
export function videoEmbedSrc(url) {
  const parsed = parseVideoUrl(url);
  return parsed ? buildEmbedSrc(parsed) : null;
}
